/**
 * Product Categories Configuration
 * Uses `code` as the primary identifier for environment consistency (dev/prod)
 * The `code` is a stable reference that doesn't change between environments,
 * unlike UUIDs which are generated per-environment.
 */

import type { Locale } from "@/i18n/routing";

/** 모든 앱 locale 의 이름이 필수 — locale 을 추가하면 빠진 항목이 컴파일 에러로 드러난다. */
export type CategoryTranslations = Record<Locale, string>;

export interface CategoryItem {
  code: string;
  level: 1 | 2 | 3;
  parent_code: string | null;
  display_order: number;
  is_active: boolean;
  is_promoted?: boolean;
  /** 추천(HOT) 노출 순서 — is_promoted 인 항목끼리만 비교. 없으면 display_order. */
  promoted_order?: number;
  names: CategoryTranslations;
}

/**
 * All categories in a flat array structure
 * Primary key: `code` (stable across environments)
 */
export const CATEGORIES: CategoryItem[] = [
  // ===== Level 1 Categories =====
  {
    code: "10",
    level: 1,
    parent_code: null,
    display_order: 1,
    is_active: true,
    names: {
      ko: "여행·액티비티",
      en: "Travel & Activities",
      ja: "旅行・アクティビティ",
      "zh-CN": "旅行与活动",
      "zh-TW": "旅行與活動",
    },
  },
  {
    code: "20",
    level: 1,
    parent_code: null,
    display_order: 2,
    is_active: true,
    names: {
      ko: "테크·스마트기기",
      en: "Tech & Smart Devices",
      ja: "テック・スマートデバイス",
      "zh-CN": "科技与智能设备",
      "zh-TW": "科技與智能設備",
    },
  },
  {
    code: "30",
    level: 1,
    parent_code: null,
    display_order: 3,
    is_active: true,
    names: {
      ko: "뷰티·홈리빙",
      en: "Beauty & Home Living",
      ja: "ビューティー・ホームリビング",
      "zh-CN": "美妆与家居生活",
      "zh-TW": "美妝與家居生活",
    },
  },
  {
    code: "40",
    level: 1,
    parent_code: null,
    display_order: 4,
    is_active: true,
    names: {
      ko: "키즈·패밀리",
      en: "Kids & Family",
      ja: "キッズ・ファミリー",
      "zh-CN": "儿童与家庭",
      "zh-TW": "兒童與家庭",
    },
  },
  {
    code: "50",
    level: 1,
    parent_code: null,
    display_order: 5,
    is_active: true,
    names: {
      ko: "모빌리티",
      en: "Mobility",
      ja: "モビリティ",
      "zh-CN": "出行与交通",
      "zh-TW": "出行與交通",
    },
  },

  // ===== Level 2: 여행·액티비티 (10) =====
  {
    code: "10-01",
    level: 2,
    parent_code: "10",
    display_order: 1,
    is_active: true,
    names: {
      ko: "카메라·드론",
      en: "Camera & Drone",
      ja: "カメラ・ドローン",
      "zh-CN": "相机与无人机",
      "zh-TW": "相機與無人機",
    },
  },
  {
    code: "10-02",
    level: 2,
    parent_code: "10",
    display_order: 2,
    is_active: true,
    names: {
      ko: "스노클링·비치",
      en: "Snorkeling & Beach",
      ja: "シュノーケリング・ビーチ",
      "zh-CN": "浮潜与沙滩用品",
      "zh-TW": "浮潛與沙灘用品",
    },
  },
  {
    code: "10-03",
    level: 2,
    parent_code: "10",
    display_order: 3,
    is_active: true,
    names: {
      ko: "스키·스노우보드",
      en: "Ski & Snowboard",
      ja: "スキー・スノーボード",
      "zh-CN": "滑雪与单板",
      "zh-TW": "滑雪與單板",
    },
  },
  {
    code: "10-04",
    level: 2,
    parent_code: "10",
    display_order: 4,
    is_active: true,
    is_promoted: true,
    promoted_order: 7,
    names: {
      ko: "캠핑",
      en: "Camping",
      ja: "キャンプ",
      "zh-CN": "露营",
      "zh-TW": "露營",
    },
  },
  {
    code: "10-05",
    level: 2,
    parent_code: "10",
    display_order: 5,
    is_active: true,
    names: {
      ko: "의상·코스튬",
      en: "Clothing & Costume",
      ja: "衣装・コスチューム",
      "zh-CN": "服饰与演出服",
      "zh-TW": "服飾與演出服",
    },
  },
  {
    code: "10-06",
    level: 2,
    parent_code: "10",
    display_order: 6,
    is_active: true,
    is_promoted: true,
    promoted_order: 2,
    names: {
      ko: "K-pop·공연",
      en: "K-pop & Concert",
      ja: "K-POP・コンサート",
      "zh-CN": "K-pop与演出",
      "zh-TW": "K-pop與演出",
    },
  },

  // ===== Level 2: 테크·스마트기기 (20) =====
  {
    code: "20-01",
    level: 2,
    parent_code: "20",
    display_order: 1,
    is_active: true,
    is_promoted: true,
    promoted_order: 1,
    names: {
      ko: "스마트폰",
      en: "Smartphone",
      ja: "スマートフォン",
      "zh-CN": "智能手机",
      "zh-TW": "智慧型手機",
    },
  },
  {
    code: "20-02",
    level: 2,
    parent_code: "20",
    display_order: 2,
    is_active: true,
    names: {
      ko: "태블릿·노트북",
      en: "Tablet & Laptop",
      ja: "タブレット・ノートPC",
      "zh-CN": "平板与笔记本",
      "zh-TW": "平板與筆記型電腦",
    },
  },
  {
    code: "20-03",
    level: 2,
    parent_code: "20",
    display_order: 3,
    is_active: true,
    names: {
      ko: "와이파이·공유기",
      en: "Wi-Fi & Router",
      ja: "Wi-Fi・ルーター",
      "zh-CN": "Wi-Fi与路由器",
      "zh-TW": "Wi-Fi與路由器",
    },
  },
  {
    code: "20-04",
    level: 2,
    parent_code: "20",
    display_order: 4,
    is_active: true,
    names: {
      ko: "오디오·마이크",
      en: "Audio & Mic",
      ja: "オーディオ・マイク",
      "zh-CN": "音频与麦克风",
      "zh-TW": "音訊與麥克風",
    },
  },
  {
    code: "20-05",
    level: 2,
    parent_code: "20",
    display_order: 5,
    is_active: true,
    names: {
      ko: "게임",
      en: "Game",
      ja: "ゲーム",
      "zh-CN": "游戏",
      "zh-TW": "遊戲",
    },
  },

  // ===== Level 2: 뷰티·홈리빙 (30) =====
  {
    code: "30-01",
    level: 2,
    parent_code: "30",
    display_order: 1,
    is_active: true,
    names: {
      ko: "뷰티기기",
      en: "Beauty Tools",
      ja: "ビューティー機器",
      "zh-CN": "美容仪器",
      "zh-TW": "美容儀器",
    },
  },
  {
    code: "30-02",
    level: 2,
    parent_code: "30",
    display_order: 2,
    is_active: true,
    names: {
      ko: "청소",
      en: "Cleaning",
      ja: "クリーニング",
      "zh-CN": "清洁",
      "zh-TW": "清潔",
    },
  },
  {
    code: "30-03",
    level: 2,
    parent_code: "30",
    display_order: 3,
    is_active: true,
    names: {
      ko: "생활가전",
      en: "Home Appliances",
      ja: "生活家電",
      "zh-CN": "生活家电",
      "zh-TW": "生活家電",
    },
  },

  // ===== Level 2: 키즈·패밀리 (40) =====
  {
    code: "40-01",
    level: 2,
    parent_code: "40",
    display_order: 1,
    is_active: true,
    names: {
      ko: "유아용품",
      en: "Baby Items",
      ja: "ベビー用品",
      "zh-CN": "婴儿用品",
      "zh-TW": "嬰兒用品",
    },
  },

  // ===== Level 2: 모빌리티 (50) =====
  {
    code: "50-01",
    level: 2,
    parent_code: "50",
    display_order: 1,
    is_active: true,
    names: {
      ko: "스쿠터·바이크",
      en: "Scooters & Bikes",
      ja: "スクーター・バイク",
      "zh-CN": "踏板车 / 摩托车 / 自行车",
      "zh-TW": "速克達 / 機車 / 自行車",
    },
  },
  {
    code: "50-02",
    level: 2,
    parent_code: "50",
    display_order: 2,
    is_active: true,
    names: {
      ko: "휠체어·이동보조",
      en: "Wheelchair & Mobility Aid",
      ja: "車椅子・歩行補助",
      "zh-CN": "轮椅与助行",
      "zh-TW": "輪椅與助行",
    },
  },
  {
    code: "50-03",
    level: 2,
    parent_code: "50",
    display_order: 3,
    is_active: true,
    names: {
      ko: "자동차·캠핑카·미니차량",
      en: "Cars / Campervans / Mini Vehicles",
      ja: "自動車・キャンピングカー・小型車両",
      "zh-CN": "汽车 / 露营车 / 小型车辆",
      "zh-TW": "汽車 / 露營車 / 小型車輛",
    },
  },

  // ===== Level 3: 카메라·드론 (10-01) =====
  {
    code: "10-01-01",
    level: 3,
    parent_code: "10-01",
    display_order: 1,
    is_active: true,
    names: {
      ko: "고프로 / 액션캠",
      en: "GoPro / Action Camera",
      ja: "GoPro・アクションカメラ",
      "zh-CN": "GoPro / 运动相机",
      "zh-TW": "GoPro / 運動相機",
    },
  },
  {
    code: "10-01-02",
    level: 3,
    parent_code: "10-01",
    display_order: 2,
    is_active: true,
    names: {
      ko: "360도 카메라 / Insta360",
      en: "360° Camera / Insta360",
      ja: "360度カメラ / Insta360",
      "zh-CN": "360度相机 / Insta360",
      "zh-TW": "360度相機 / Insta360",
    },
  },
  {
    code: "10-01-03",
    level: 3,
    parent_code: "10-01",
    display_order: 3,
    is_active: true,
    names: {
      ko: "오즈모 / 짐벌",
      en: "Osmo / Gimbal",
      ja: "Osmo・ジンバル",
      "zh-CN": "Osmo / 稳定器",
      "zh-TW": "Osmo / 穩定器",
    },
  },
  {
    code: "10-01-04",
    level: 3,
    parent_code: "10-01",
    display_order: 4,
    is_active: true,
    names: {
      ko: "드론",
      en: "Drone",
      ja: "ドローン",
      "zh-CN": "无人机",
      "zh-TW": "無人機",
    },
  },
  {
    code: "10-01-05",
    level: 3,
    parent_code: "10-01",
    display_order: 5,
    is_active: true,
    names: {
      ko: "미러리스 / 디지털카메라",
      en: "Mirrorless / Digital Camera",
      ja: "ミラーレス / デジカメ",
      "zh-CN": "微单 / 数码相机",
      "zh-TW": "微單 / 數碼相機",
    },
  },
  {
    code: "10-01-06",
    level: 3,
    parent_code: "10-01",
    display_order: 6,
    is_active: true,
    names: {
      ko: "즉석카메라 (폴라로이드)",
      en: "Instant Camera (Polaroid)",
      ja: "インスタントカメラ（ポラロイド）",
      "zh-CN": "拍立得相机",
      "zh-TW": "拍立得相機",
    },
  },

  // ===== Level 3: 스노클링·비치 (10-02) =====
  {
    code: "10-02-01",
    level: 3,
    parent_code: "10-02",
    display_order: 1,
    is_active: true,
    names: {
      ko: "스노클링 세트",
      en: "Snorkeling Set",
      ja: "シュノーケリングセット",
      "zh-CN": "浮潜套装",
      "zh-TW": "浮潛套裝",
    },
  },
  {
    code: "10-02-02",
    level: 3,
    parent_code: "10-02",
    display_order: 2,
    is_active: true,
    names: {
      ko: "다이빙 장비",
      en: "Diving Gear",
      ja: "ダイビング用品",
      "zh-CN": "潜水装备",
      "zh-TW": "潛水裝備",
    },
  },
  {
    code: "10-02-03",
    level: 3,
    parent_code: "10-02",
    display_order: 3,
    is_active: true,
    names: {
      ko: "비치 텐트 / 그늘막",
      en: "Beach Tent / Sunshade",
      ja: "ビーチテント / サンシェード",
      "zh-CN": "沙滩帐篷 / 遮阳棚",
      "zh-TW": "沙灘帳篷 / 遮陽棚",
    },
  },
  {
    code: "10-02-04",
    level: 3,
    parent_code: "10-02",
    display_order: 4,
    is_active: true,
    names: {
      ko: "비치 액세서리",
      en: "Beach Accessories",
      ja: "ビーチアクセサリー",
      "zh-CN": "沙滩配件",
      "zh-TW": "沙灘配件",
    },
  },

  // ===== Level 3: 스키·스노우보드 (10-03) =====
  {
    code: "10-03-01",
    level: 3,
    parent_code: "10-03",
    display_order: 1,
    is_active: true,
    names: {
      ko: "스키 세트",
      en: "Ski Set",
      ja: "スキーセット",
      "zh-CN": "双板滑雪套装",
      "zh-TW": "雙板滑雪套裝",
    },
  },
  {
    code: "10-03-02",
    level: 3,
    parent_code: "10-03",
    display_order: 2,
    is_active: true,
    names: {
      ko: "스노보드 세트",
      en: "Snowboard Set",
      ja: "スノーボードセット",
      "zh-CN": "单板滑雪套装",
      "zh-TW": "單板滑雪套裝",
    },
  },
  {
    code: "10-03-03",
    level: 3,
    parent_code: "10-03",
    display_order: 3,
    is_active: true,
    names: {
      ko: "스키복 / 방한복",
      en: "Ski Wear / Winter Wear",
      ja: "スキーウェア / 防寒着",
      "zh-CN": "滑雪服 / 防寒服",
      "zh-TW": "滑雪服 / 防寒服",
    },
  },
  {
    code: "10-03-04",
    level: 3,
    parent_code: "10-03",
    display_order: 4,
    is_active: true,
    names: {
      ko: "고글 / 헬멧",
      en: "Goggles / Helmet",
      ja: "ゴーグル / ヘルメット",
      "zh-CN": "护目镜 / 头盔",
      "zh-TW": "護目鏡 / 頭盔",
    },
  },

  // ===== Level 3: 캠핑 (10-04) =====
  {
    code: "10-04-01",
    level: 3,
    parent_code: "10-04",
    display_order: 1,
    is_active: true,
    names: {
      ko: "백패킹 세트",
      en: "Backpacking Set",
      ja: "バックパックセット",
      "zh-CN": "徒步露营套装",
      "zh-TW": "徒步露營套裝",
    },
  },
  {
    code: "10-04-02",
    level: 3,
    parent_code: "10-04",
    display_order: 2,
    is_active: true,
    is_promoted: true,
    promoted_order: 6,
    names: {
      ko: "텐트 세트",
      en: "Tent Set",
      ja: "テントセット",
      "zh-CN": "帐篷套装",
      "zh-TW": "帳篷套裝",
    },
  },
  {
    code: "10-04-03",
    level: 3,
    parent_code: "10-04",
    display_order: 3,
    is_active: true,
    names: {
      ko: "차박 세트",
      en: "Car Camping Set",
      ja: "車中泊セット",
      "zh-CN": "车载露营套装",
      "zh-TW": "車載露營套裝",
    },
  },

  // ===== Level 3: 의상·코스튬 (10-05) =====
  {
    code: "10-05-01",
    level: 3,
    parent_code: "10-05",
    display_order: 1,
    is_active: true,
    is_promoted: true,
    promoted_order: 3,
    names: {
      ko: "전통의상 (한복/기모노 등)",
      en: "Traditional Clothing (Hanbok/Kimono etc.)",
      ja: "伝統衣装（チマチョゴリ・着物など）",
      "zh-CN": "传统服饰（韩服/和服等）",
      "zh-TW": "傳統服飾（韓服/和服等）",
    },
  },
  {
    code: "10-05-02",
    level: 3,
    parent_code: "10-05",
    display_order: 2,
    is_active: true,
    names: {
      ko: "코스튬",
      en: "Costume",
      ja: "コスチューム",
      "zh-CN": "演出服",
      "zh-TW": "演出服",
    },
  },
  {
    code: "10-05-03",
    level: 3,
    parent_code: "10-05",
    display_order: 3,
    is_active: true,
    names: {
      ko: "테마 의상",
      en: "Theme Outfit",
      ja: "テーマ衣装",
      "zh-CN": "主题服装",
      "zh-TW": "主題服裝",
    },
  },

  // ===== Level 3: K-pop·공연 (10-06) =====
  {
    code: "10-06-01",
    level: 3,
    parent_code: "10-06",
    display_order: 1,
    is_active: true,
    names: {
      ko: "응원봉",
      en: "Light Stick",
      ja: "ペンライト",
      "zh-CN": "应援棒",
      "zh-TW": "應援棒",
    },
  },

  // ===== Level 3: 스마트폰 (20-01) =====
  {
    code: "20-01-01",
    level: 3,
    parent_code: "20-01",
    display_order: 1,
    is_active: true,
    names: {
      ko: "삼성 갤럭시",
      en: "Samsung Galaxy",
      ja: "Samsung Galaxy",
      "zh-CN": "三星Galaxy",
      "zh-TW": "三星Galaxy",
    },
  },
  {
    code: "20-01-02",
    level: 3,
    parent_code: "20-01",
    display_order: 2,
    is_active: true,
    names: {
      ko: "아이폰",
      en: "iPhone",
      ja: "iPhone",
      "zh-CN": "iPhone",
      "zh-TW": "iPhone",
    },
  },
  {
    code: "20-01-03",
    level: 3,
    parent_code: "20-01",
    display_order: 3,
    is_active: true,
    names: {
      ko: "기타 안드로이드폰",
      en: "Other Android Phone",
      ja: "その他Androidスマホ",
      "zh-CN": "其他安卓手机",
      "zh-TW": "其他安卓手機",
    },
  },

  // ===== Level 3: 태블릿·노트북 (20-02) =====
  {
    code: "20-02-01",
    level: 3,
    parent_code: "20-02",
    display_order: 1,
    is_active: true,
    names: {
      ko: "아이패드",
      en: "iPad",
      ja: "iPad",
      "zh-CN": "iPad",
      "zh-TW": "iPad",
    },
  },
  {
    code: "20-02-02",
    level: 3,
    parent_code: "20-02",
    display_order: 2,
    is_active: true,
    names: {
      ko: "갤럭시 탭",
      en: "Galaxy Tab",
      ja: "Galaxy Tab",
      "zh-CN": "Galaxy Tab",
      "zh-TW": "Galaxy Tab",
    },
  },
  {
    code: "20-02-03",
    level: 3,
    parent_code: "20-02",
    display_order: 3,
    is_active: true,
    names: {
      ko: "노트북 / 맥북",
      en: "Laptop / MacBook",
      ja: "ノートPC / MacBook",
      "zh-CN": "笔记本 / MacBook",
      "zh-TW": "筆記型電腦 / MacBook",
    },
  },
  {
    code: "20-02-04",
    level: 3,
    parent_code: "20-02",
    display_order: 4,
    is_active: true,
    names: {
      ko: "베이비 모니터",
      en: "Baby Monitor",
      ja: "ベビーモニター",
      "zh-CN": "婴儿监视器",
      "zh-TW": "嬰兒監視器",
    },
  },

  // ===== Level 3: 와이파이·공유기 (20-03) =====
  {
    code: "20-03-01",
    level: 3,
    parent_code: "20-03",
    display_order: 1,
    is_active: true,
    names: {
      ko: "포켓 와이파이",
      en: "Pocket Wi-Fi",
      ja: "ポケットWi-Fi",
      "zh-CN": "随身Wi-Fi",
      "zh-TW": "隨身Wi-Fi",
    },
  },
  {
    code: "20-03-02",
    level: 3,
    parent_code: "20-03",
    display_order: 2,
    is_active: true,
    names: {
      ko: "가정용 공유기",
      en: "Home Router",
      ja: "家庭用ルーター",
      "zh-CN": "家用路由器",
      "zh-TW": "家用路由器",
    },
  },

  // ===== Level 3: 오디오·마이크 (20-04) =====
  {
    code: "20-04-01",
    level: 3,
    parent_code: "20-04",
    display_order: 1,
    is_active: true,
    names: {
      ko: "블루투스 스피커",
      en: "Bluetooth Speaker",
      ja: "Bluetoothスピーカー",
      "zh-CN": "蓝牙音箱",
      "zh-TW": "藍牙音箱",
    },
  },
  {
    code: "20-04-02",
    level: 3,
    parent_code: "20-04",
    display_order: 2,
    is_active: true,
    names: {
      ko: "마이크",
      en: "Microphone",
      ja: "マイク",
      "zh-CN": "麦克风",
      "zh-TW": "麥克風",
    },
  },
  {
    code: "20-04-03",
    level: 3,
    parent_code: "20-04",
    display_order: 3,
    is_active: true,
    names: {
      ko: "헤드셋 / 이어폰",
      en: "Headset / Earbuds",
      ja: "ヘッドセット / イヤホン",
      "zh-CN": "耳机 / 入耳式耳机",
      "zh-TW": "耳機 / 入耳式耳機",
    },
  },

  // ===== Level 3: 게임 (20-05) =====
  {
    code: "20-05-01",
    level: 3,
    parent_code: "20-05",
    display_order: 1,
    is_active: true,
    names: {
      ko: "PS5",
      en: "PlayStation 5",
      ja: "PlayStation 5",
      "zh-CN": "PlayStation 5",
      "zh-TW": "PlayStation 5",
    },
  },
  {
    code: "20-05-02",
    level: 3,
    parent_code: "20-05",
    display_order: 2,
    is_active: true,
    names: {
      ko: "닌텐도 스위치",
      en: "Nintendo Switch",
      ja: "Nintendo Switch",
      "zh-CN": "任天堂Switch",
      "zh-TW": "任天堂Switch",
    },
  },
  {
    code: "20-05-03",
    level: 3,
    parent_code: "20-05",
    display_order: 3,
    is_active: true,
    names: {
      ko: "스팀덱",
      en: "Steam Deck",
      ja: "Steam Deck",
      "zh-CN": "Steam Deck掌机",
      "zh-TW": "Steam Deck掌機",
    },
  },

  // ===== Level 3: 뷰티기기 (30-01) =====
  {
    code: "30-01-01",
    level: 3,
    parent_code: "30-01",
    display_order: 1,
    is_active: true,
    names: {
      ko: "드라이기",
      en: "Hair Dryer",
      ja: "ドライヤー",
      "zh-CN": "吹风机",
      "zh-TW": "吹風機",
    },
  },
  {
    code: "30-01-02",
    level: 3,
    parent_code: "30-01",
    display_order: 2,
    is_active: true,
    names: {
      ko: "고데기 / 스타일러",
      en: "Hair Iron / Styler",
      ja: "ヘアアイロン / スタイラー",
      "zh-CN": "卷发棒 / 造型器",
      "zh-TW": "捲髮棒 / 造型器",
    },
  },
  {
    code: "30-01-03",
    level: 3,
    parent_code: "30-01",
    display_order: 3,
    is_active: true,
    names: {
      ko: "페이셜 / 뷰티 디바이스",
      en: "Facial / Beauty Device",
      ja: "フェイシャル・美顔器",
      "zh-CN": "面部美容仪",
      "zh-TW": "面部美容儀",
    },
  },

  // ===== Level 3: 청소 (30-02) =====
  {
    code: "30-02-01",
    level: 3,
    parent_code: "30-02",
    display_order: 1,
    is_active: true,
    names: {
      ko: "무선청소기",
      en: "Cordless Vacuum",
      ja: "コードレス掃除機",
      "zh-CN": "无线吸尘器",
      "zh-TW": "無線吸塵器",
    },
  },
  {
    code: "30-02-02",
    level: 3,
    parent_code: "30-02",
    display_order: 2,
    is_active: true,
    names: {
      ko: "다이슨 청소기",
      en: "Dyson Vacuum",
      ja: "Dyson掃除機",
      "zh-CN": "戴森吸尘器",
      "zh-TW": "戴森吸塵器",
    },
  },
  {
    code: "30-02-03",
    level: 3,
    parent_code: "30-02",
    display_order: 3,
    is_active: true,
    names: {
      ko: "청소 액세서리",
      en: "Cleaning Accessories",
      ja: "清掃アクセサリー",
      "zh-CN": "清洁配件",
      "zh-TW": "清潔配件",
    },
  },

  // ===== Level 3: 생활가전 (30-03) =====
  {
    code: "30-03-01",
    level: 3,
    parent_code: "30-03",
    display_order: 1,
    is_active: true,
    names: {
      ko: "가습기",
      en: "Humidifier",
      ja: "加湿器",
      "zh-CN": "加湿器",
      "zh-TW": "加濕器",
    },
  },
  {
    code: "30-03-02",
    level: 3,
    parent_code: "30-03",
    display_order: 2,
    is_active: true,
    names: {
      ko: "조명 / 스탠드",
      en: "Lighting / Stand",
      ja: "照明 / スタンドライト",
      "zh-CN": "照明 / 台灯",
      "zh-TW": "照明 / 檯燈",
    },
  },
  {
    code: "30-03-03",
    level: 3,
    parent_code: "30-03",
    display_order: 3,
    is_active: true,
    names: {
      ko: "다리미",
      en: "Iron",
      ja: "アイロン",
      "zh-CN": "熨斗/熨斗机",
      "zh-TW": "熨斗",
    },
  },

  // ===== Level 3: 유아용품 (40-01) =====
  {
    code: "40-01-01",
    level: 3,
    parent_code: "40-01",
    display_order: 1,
    is_active: true,
    names: {
      ko: "유모차",
      en: "Stroller",
      ja: "ベビーカー",
      "zh-CN": "婴儿推车",
      "zh-TW": "嬰兒推車",
    },
  },
  {
    code: "40-01-02",
    level: 3,
    parent_code: "40-01",
    display_order: 2,
    is_active: true,
    names: {
      ko: "카시트",
      en: "Car Seat",
      ja: "チャイルドシート",
      "zh-CN": "儿童安全座椅",
      "zh-TW": "兒童安全座椅",
    },
  },
  {
    code: "40-01-03",
    level: 3,
    parent_code: "40-01",
    display_order: 3,
    is_active: true,
    names: {
      ko: "아기띠 / 캐리어",
      en: "Baby Carrier",
      ja: "抱っこ紐 / ベビーキャリア",
      "zh-CN": "婴儿背带",
      "zh-TW": "嬰兒背帶",
    },
  },

  // ===== Level 3: 모빌리티 — 자동차는 50-03 아래로 이동 =====
  {
    code: "50-03-03",
    level: 3,
    parent_code: "50-03",
    display_order: 1,
    is_active: true,
    is_promoted: true,
    promoted_order: 8,
    names: {
      ko: "자동차",
      en: "Car",
      ja: "自動車",
      "zh-CN": "汽车",
      "zh-TW": "汽車",
    },
  },
  {
    // 09-28 오토바이(50-01-05)를 이 코드로 합쳤다 — 이름도 통합. 50-01-05 는 비활성.
    code: "50-01-02",
    level: 3,
    parent_code: "50-01",
    display_order: 2,
    is_active: true,
    is_promoted: true,
    promoted_order: 5,
    names: {
      ko: "오토바이·스쿠터",
      en: "Motorcycles & scooters",
      ja: "バイク・スクーター",
      "zh-CN": "摩托车·踏板车",
      "zh-TW": "機車·速克達",
    },
  },
  {
    code: "50-01-03",
    level: 3,
    parent_code: "50-01",
    display_order: 3,
    is_active: true,
    is_promoted: true,
    promoted_order: 4,
    names: {
      ko: "전기자전거 / 자전거",
      en: "E-Bike / Bicycle",
      ja: "電動自転車 / 自転車",
      "zh-CN": "电动自行车 / 自行车",
      "zh-TW": "電動自行車 / 自行車",
    },
  },
  {
    code: "50-01-04",
    level: 3,
    parent_code: "50-01",
    display_order: 4,
    is_active: true,
    names: {
      ko: "전동 킥보드",
      en: "E-Kick Scooter",
      ja: "電動キックボード",
      "zh-CN": "电动滑板车",
      "zh-TW": "電動滑板車",
    },
  },
  {
    // 09-28 50-01-02(오토바이·스쿠터)로 합쳐 비활성 — 옛 링크는 normalizeCategoryCodes 가 옮긴다.
    code: "50-01-05",
    level: 3,
    parent_code: "50-01",
    display_order: 5,
    is_active: false,
    names: {
      ko: "오토바이",
      en: "Motorcycle",
      ja: "バイク",
      "zh-CN": "摩托车",
      "zh-TW": "摩托車",
    },
  },

  // ===== Level 3: 휠체어·이동보조 (50-02) =====
  {
    code: "50-02-01",
    level: 3,
    parent_code: "50-02",
    display_order: 1,
    is_active: true,
    names: {
      ko: "휠체어",
      en: "Wheelchair",
      ja: "車椅子",
      "zh-CN": "轮椅",
      "zh-TW": "輪椅",
    },
  },
  {
    code: "50-02-02",
    level: 3,
    parent_code: "50-02",
    display_order: 2,
    is_active: true,
    names: {
      ko: "보행 보조기",
      en: "Walker / Mobility Aid",
      ja: "歩行補助器具",
      "zh-CN": "助行器",
      "zh-TW": "助行器",
    },
  },
  {
    code: "50-02-03",
    level: 3,
    parent_code: "50-02",
    display_order: 3,
    is_active: true,
    names: {
      ko: "전동 휠체어",
      en: "Electric Wheelchair",
      ja: "電動車椅子",
      "zh-CN": "电动轮椅",
      "zh-TW": "電動輪椅",
    },
  },

  // ===== Level 3: 자동차·캠핑카·미니차량 (50-03) =====
  {
    code: "50-03-01",
    level: 3,
    parent_code: "50-03",
    display_order: 2,
    is_active: true,
    names: {
      ko: "캠핑카",
      en: "Camping Van",
      ja: "キャンピングカー",
      "zh-CN": "露营车",
      "zh-TW": "露營車",
    },
  },
  {
    code: "50-03-02",
    level: 3,
    parent_code: "50-03",
    display_order: 3,
    is_active: true,
    names: {
      ko: "미니차량",
      en: "Mini Vehicle",
      ja: "小型車両",
      "zh-CN": "小型车",
      "zh-TW": "小型車",
    },
  },
];

/**
 * Helper functions to get categories by level and parent
 */

export function getPromotedCategories(): CategoryItem[] {
  return CATEGORIES.filter((cat) => cat.is_promoted && cat.is_active).sort(
    (a, b) =>
      (a.promoted_order ?? a.display_order) -
      (b.promoted_order ?? b.display_order),
  );
}

export function getLevel1Categories(): CategoryItem[] {
  return CATEGORIES.filter((cat) => cat.level === 1 && cat.is_active).sort(
    (a, b) => a.display_order - b.display_order,
  );
}

export function getLevel2Categories(parentCode: string): CategoryItem[] {
  return CATEGORIES.filter(
    (cat) => cat.level === 2 && cat.parent_code === parentCode && cat.is_active,
  ).sort((a, b) => a.display_order - b.display_order);
}

export function getLevel3Categories(parentCode: string): CategoryItem[] {
  return CATEGORIES.filter(
    (cat) => cat.level === 3 && cat.parent_code === parentCode && cat.is_active,
  ).sort((a, b) => a.display_order - b.display_order);
}

/**
 * 합쳐서 없어진 코드 → 이어받은 코드(09-28). 옛 링크·북마크·배너의 `categories=` 를
 * 새 코드로 읽기 위한 것 — 상품 데이터는 이미 옮겼다.
 */
const CATEGORY_CODE_ALIASES: Record<string, string> = {
  "50-01-01": "50-03-03", // 자동차 → 자동차·캠핑카·미니차량 중분류
  "50-01-05": "50-01-02", // 오토바이 → 오토바이·스쿠터
};

/** URL 등에서 읽은 카테고리 코드 목록을 현재 코드로(별칭 치환 + 중복 제거, 순서 유지). */
export function normalizeCategoryCodes(codes: string[]): string[] {
  return [...new Set(codes.map((c) => CATEGORY_CODE_ALIASES[c] ?? c))];
}

/**
 * Get category by code (primary lookup method)
 */
export function getCategoryByCode(code: string): CategoryItem | undefined {
  return CATEGORIES.find((cat) => cat.code === (CATEGORY_CODE_ALIASES[code] ?? code));
}

/**
 * Get category name by locale
 */
export function getCategoryName(
  category: CategoryItem,
  locale: Locale,
): string {
  return category.names[locale];
}

/**
 * Get full category path (Level 1 > Level 2 > Level 3)
 * @param categoryCode - The category code (e.g., "10-01-01")
 * @param locale - The locale for translations
 */
export function getCategoryPath(
  categoryCode: string,
  locale: Locale,
): string {
  const category = getCategoryByCode(categoryCode);
  if (!category) return "";

  const path: string[] = [];

  // Add current category
  path.push(getCategoryName(category, locale));

  // Traverse up to find parent categories
  let currentCat = category;
  while (currentCat.parent_code) {
    const parent = getCategoryByCode(currentCat.parent_code);
    if (!parent) break;
    path.unshift(getCategoryName(parent, locale));
    currentCat = parent;
  }

  return path.join(" > ");
}

/**
 * Get children categories of a given code
 */
export function getChildCategories(parentCode: string): CategoryItem[] {
  return CATEGORIES.filter(
    (cat) => cat.parent_code === parentCode && cat.is_active,
  ).sort((a, b) => a.display_order - b.display_order);
}

/**
 * Check if a category has children
 */
export function hasChildren(code: string): boolean {
  return CATEGORIES.some((cat) => cat.parent_code === code && cat.is_active);
}

/**
 * 선택된 카테고리 코드들을 **자기 자신 + 모든 자손** 으로 펼친다.
 *
 * 검색 서버 필터(`category_code in (...)`) 전용이다. 클라이언트 필터
 * (`matchesSelectedCategories`)는 반대로 상품의 카테고리에서 **부모를 거슬러
 * 올라가며** 선택 여부를 본다 — 즉 "L1 '50' 선택 = 50-xx-xx 전부"다. 서버에서
 * 같은 판정을 하려면 내려가면서 펼치는 수밖에 없다(`.in()` 은 prefix 매칭이
 * 아니다). 두 판정이 어긋나면 서버가 먼저 버린 상품을 클라가 되살릴 방법이 없다.
 *
 * `is_active` 로 거르지 않는 이유: 서버 필터는 **클라이언트 필터보다 넓어야**
 * 안전하다. 비활성 자식 밑의 상품을 서버가 버리면 화면에서 영영 사라지지만,
 * 남겨 두면 클라이언트 필터가 최종 판정을 한다. (현재 비활성 카테고리는 0건)
 *
 * 설정에 없는 코드(신규·오타)는 그대로 통과시킨다 — 그 코드와 정확히 같은
 * 상품은 클라이언트 필터도 통과시키므로 동작이 일치한다.
 */
export function expandCategoryCodes(codes: string[]): string[] {
  const out = new Set<string>();
  const queue = [...new Set(codes.map((c) => c.trim()).filter(Boolean))];

  while (queue.length > 0) {
    const code = queue.pop()!;
    if (out.has(code)) continue; // 순환 참조 방어
    out.add(code);
    for (const child of CATEGORIES) {
      if (child.parent_code === code && !out.has(child.code)) {
        queue.push(child.code);
      }
    }
  }

  return [...out];
}
